import {
  ApartmentOutlined,
  ArrowRightOutlined,
  MinusSquareOutlined,
  PlusSquareOutlined,
  UnorderedListOutlined,
} from "@ant-design/icons";
import {
  Button,
  Card,
  Col,
  Input,
  Row,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Typography,
} from "antd";
import React, { useCallback, useEffect, useState } from "react";
import { connect, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import MainLayout from "../../../components/layouts/MainLayout";
import TopContent from "../../../components/layouts/TopContent";
import StatusSelect from "../../../components/StatusSelect";
import appLabel from "../../../config/appLabel";
import appStatic from "../../../config/appStatic";
import { rightAction } from "../../../redux/actions";
import definedApi from "../../../services/definedApi";
import cnt from "../../../services/definedConstant";
import { BOM } from "../../../services/definedMethod";

const { Text, Link, Title } = Typography;
const { manageGeneralLedgerItems } = appStatic;

const TYPE_ORDER = ["ASSET", "LIABILITY", "EQUITY", "INCOME", "EXPENSE"];

function buildTree(rawList) {
  if (!rawList || rawList.length === 0) return [];

  // 1. Index by id
  const byId = {};
  rawList.forEach((item) => {
    byId[item.id] = { ...item, children: [], collapsed: true };
  });

  // 2. Wire children; collect root-level nodes per type
  const typeGroups = {}; // { "ASSET": [node, ...], ... }

  rawList.forEach((item) => {
    const node = byId[item.id];
    const parentExists = item.parentId != null && byId[item.parentId];

    if (parentExists) {
      byId[item.parentId].children.push(node);
    } else {
      // Root of its type group
      // const typeName = item.type?.value || "OTHER";
      const typeName = item.type?.value?.toUpperCase() || "OTHER";
      if (!typeGroups[typeName]) typeGroups[typeName] = [];
      typeGroups[typeName].push(node);
    }
  });

  // 3. Sort siblings by glCode (standard COA ordering)
  const sortByGlCode = (arr) =>
    arr.sort((a, b) => String(a.glCode).localeCompare(String(b.glCode)));

  Object.values(typeGroups).forEach(sortByGlCode);
  Object.values(byId).forEach((node) => sortByGlCode(node.children));

  // 4. Build virtual type-category nodes (always expanded at top level)
  const orderedTypes = [
    ...TYPE_ORDER.filter((t) => typeGroups[t]),
    ...Object.keys(typeGroups).filter((t) => !TYPE_ORDER.includes(t)),
  ];

  // const typeNodes = orderedTypes.map((typeName) => ({
  //   id: `__type__${typeName}`,
  //   isTypeNode: true,
  //   typeName,
  //   name: typeName,
  //   children: typeGroups[typeName] || [],
  //   collapsed: false, // type-category nodes start open
  // }));
  const typeNodes = orderedTypes.map((typeName) => ({
    id: `__type__${typeName}`,
    isTypeNode: true,
    typeName,
    name: typeName.charAt(0) + typeName.slice(1).toLowerCase(),
    children: typeGroups[typeName] || [],
    collapsed: false,
  }));
  // 5. Virtual root
  return [
    {
      id: "__root__",
      isRoot: true,
      name: "Accounting",
      children: typeNodes,
      collapsed: false,
    },
  ];
}

// Flatten all nodes (for expand/collapse all)
function flattenTree(nodes, out = []) {
  nodes.forEach((n) => {
    out.push(n);
    if (n.children?.length) flattenTree(n.children, out);
  });
  return out;
}

// Deep-clone (needed for immutable React state)
function deepClone(nodes) {
  return nodes.map((n) => ({ ...n, children: deepClone(n.children || []) }));
}

// Toggle a single node by id
function toggleById(nodes, targetId) {
  return nodes.map((n) => {
    if (n.id === targetId)
      return { ...n, collapsed: !n.collapsed, children: deepClone(n.children) };
    return { ...n, children: toggleById(n.children || [], targetId) };
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// TreeNode component
// ─────────────────────────────────────────────────────────────────────────────
const TreeNode = ({ node, depth = 0, onToggle, onSelectNode, selectedId }) => {
  const hasChildren = node.children?.length > 0;
  const isVirtual = node.isRoot || node.isTypeNode;
  const isSelected = !isVirtual && selectedId === node.id;

  const handleToggle = (e) => {
    e.stopPropagation();
    if (hasChildren) onToggle(node.id);
  };
  const handleSelect = () => {
    if (!isVirtual) onSelectNode(node);
  };

  // ── Root node — section header ─────────────────────────────────────────
  if (node.isRoot) {
    return (
      <div style={{ marginBottom: 8 }}>
        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            color: "#9ca3af",
            letterSpacing: 1,
            textTransform: "uppercase",
            padding: "4px 8px 8px",
          }}
        >
          {node.name}
        </div>
        {node.children.map((child) => (
          <TreeNode
            key={child.id}
            node={child}
            depth={0}
            onToggle={onToggle}
            onSelectNode={onSelectNode}
            selectedId={selectedId}
          />
        ))}
      </div>
    );
  }

  // ── Type node — pill header ────────────────────────────────────────────
  if (node.isTypeNode) {
    return (
      <div style={{ marginBottom: 6 }}>
        <div
          onClick={handleToggle}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: "8px 12px",
            marginBottom: 4,
            borderRadius: 8,
            background:
              "linear-gradient(135deg, rgba(21,204,174,0.15) 0%, rgba(79,236,160,0.15) 100%)",
            border: "1px solid rgba(21,204,174,0.25)",
            cursor: hasChildren ? "pointer" : "default",
            userSelect: "none",
          }}
        >
          <span
            style={{
              width: 18,
              display: "flex",
              alignItems: "center",
              flexShrink: 0,
            }}
          >
            {hasChildren ? (
              node.collapsed ? (
                <PlusSquareOutlined
                  style={{ color: "#15CCAE", fontSize: 13 }}
                />
              ) : (
                <MinusSquareOutlined
                  style={{ color: "#15CCAE", fontSize: 13 }}
                />
              )
            ) : null}
          </span>
          <span
            style={{ fontWeight: 700, fontSize: 12, color: "#0d9488", flex: 1 }}
          >
            {node.name}
          </span>
          {hasChildren && (
            <span
              style={{
                background: "#15CCAE",
                color: "#000",
                fontSize: 10,
                fontWeight: 700,
                borderRadius: 20,
                padding: "1px 7px",
                minWidth: 20,
                textAlign: "center",
              }}
            >
              {node.children.length}
            </span>
          )}
        </div>
        {hasChildren && !node.collapsed && (
          <div
            style={{
              paddingLeft: 16,
              borderLeft: "2px solid rgba(21,204,174,0.2)",
              marginLeft: 8,
              marginBottom: 4,
            }}
          >
            {node.children.map((child) => (
              <TreeNode
                key={child.id}
                node={child}
                depth={depth + 1}
                onToggle={onToggle}
                onSelectNode={onSelectNode}
                selectedId={selectedId}
              />
            ))}
          </div>
        )}
      </div>
    );
  }

  // ── Regular node — card row ────────────────────────────────────────────
  return (
    <div style={{ marginBottom: 3 }}>
      <div
        onClick={hasChildren ? handleToggle : handleSelect}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "7px 12px",
          borderRadius: 6,
          cursor: "pointer",
          background: isSelected ? "rgba(21,204,174,0.1)" : "#ffffff",
          border: isSelected
            ? "1px solid rgba(21,204,174,0.4)"
            : "1px solid #f0f0f0",
          boxShadow: isSelected
            ? "0 1px 4px rgba(21,204,174,0.15)"
            : "0 1px 2px rgba(0,0,0,0.04)",
          transition: "all 0.15s",
        }}
        onMouseEnter={(e) => {
          if (!isSelected) e.currentTarget.style.background = "#f9fafb";
        }}
        onMouseLeave={(e) => {
          if (!isSelected) e.currentTarget.style.background = "#ffffff";
        }}
      >
        <span
          style={{
            width: 18,
            display: "flex",
            alignItems: "center",
            flexShrink: 0,
          }}
        >
          {hasChildren ? (
            node.collapsed ? (
              <PlusSquareOutlined style={{ color: "#15CCAE", fontSize: 12 }} />
            ) : (
              <MinusSquareOutlined style={{ color: "#15CCAE", fontSize: 12 }} />
            )
          ) : (
            <span
              style={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                background: isSelected ? "#15CCAE" : "#d1d5db",
                display: "inline-block",
              }}
            />
          )}
        </span>

        <span
          style={{
            flex: 1,
            fontSize: 12,
            fontWeight: isSelected ? 600 : 400,
            color: isSelected
              ? "#0d9488"
              : node.disabled
                ? "#9ca3af"
                : "#374151",
            userSelect: "none",
          }}
        >
          {node.glCode && (
            <span
              style={{
                fontFamily: "monospace",
                fontSize: 11,
                color: "#9ca3af",
                marginRight: 6,
              }}
            >
              {node.glCode}
            </span>
          )}
          {node.name}
        </span>

        {node.disabled && (
          <Tag
            color="error"
            style={{
              fontSize: 10,
              lineHeight: "16px",
              padding: "0 4px",
              margin: 0,
            }}
          >
            Disabled
          </Tag>
        )}
        {hasChildren && (
          <span
            style={{
              background: "#f3f4f6",
              color: "#6b7280",
              fontSize: 10,
              fontWeight: 600,
              borderRadius: 20,
              padding: "1px 6px",
            }}
          >
            {node.children.length}
          </span>
        )}
      </div>

      {hasChildren && !node.collapsed && (
        <div
          style={{
            paddingLeft: 16,
            borderLeft: "2px solid #f0f0f0",
            marginLeft: 8,
            marginTop: 3,
          }}
        >
          {node.children.map((child) => (
            <TreeNode
              key={child.id}
              node={child}
              depth={depth + 1}
              onToggle={onToggle}
              onSelectNode={onSelectNode}
              selectedId={selectedId}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// GL Detail Panel
// ─────────────────────────────────────────────────────────────────────────────
const GLDetailPanel = ({ node }) => {
  if (!node)
    return (
      <div
        style={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          color: "#9ca3af",
          gap: 8,
          padding: 24,
        }}
      >
        <ApartmentOutlined style={{ fontSize: 28, color: "#d1d5db" }} />
        <Text type="secondary" style={{ fontSize: 12, textAlign: "center" }}>
          Select an account in the tree to view its details
        </Text>
      </div>
    );

  const rows = [
    { name: "GL Code", labelValue: node.glCode ?? "—" },
    { name: "Account Type", labelValue: node.type?.value ?? "—" },
    { name: "Account Usage", labelValue: node.usage?.value ?? "—" },
    {
      name: "Manual Entries Allowed",
      labelValue: node.manualEntriesAllowed ? "true" : "false",
    },
    { name: "Status", labelValue: node.disabled ? "Disabled" : "Active" },
    ...(node.description
      ? [{ name: "Description", labelValue: node.description }]
      : []),
  ];

  return (
    <div
      style={{
        border: "1px solid #E5E7EB",
        borderRadius: 12,
        overflow: "hidden",
        margin: 12,
      }}
    >
      {rows.map((item, i) => (
        <div
          key={item.name}
          style={{
            display: "flex",
            alignItems: "center",
            borderBottom: i !== rows.length - 1 ? "1px solid #F3F4F6" : 0,
            minHeight: 44,
          }}
          onMouseEnter={(e) =>
            (e.currentTarget.style.backgroundColor = "#F9FAFB")
          }
          onMouseLeave={(e) =>
            (e.currentTarget.style.backgroundColor = "transparent")
          }
        >
          <div
            style={{
              flex: "0 0 45%",
              padding: "10px 14px",
              backgroundColor: "#F8FAFC",
              borderRight: "1px solid #E5E7EB",
              fontWeight: 600,
              fontSize: 12,
              color: "#374151",
              minHeight: 44,
              display: "flex",
              alignItems: "center",
            }}
          >
            {item.name}
          </div>
          <div
            style={{
              flex: "0 0 28px",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <ArrowRightOutlined style={{ fontSize: 11, color: "#9ca3af" }} />
          </div>
          <div
            style={{
              flex: 1,
              padding: "10px 10px 10px 0",
              fontSize: 12,
              color: "#111827",
              display: "flex",
              alignItems: "center",
            }}
          >
            {item.labelValue}
          </div>
        </div>
      ))}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// LIST VIEW COLUMNS — unchanged from original
// ─────────────────────────────────────────────────────────────────────────────
const columns = [
  {
    title: "Account",
    dataIndex: "account",
    sorter: (a, b) => a.account.localeCompare(b.account),
  },
  {
    title: "GL Code",
    dataIndex: "glCode",
    sorter: (a, b) => a.glCode.localeCompare(b.glCode),
  },
  {
    title: "Account Type",
    dataIndex: "accountType",
  },
  {
    title: "Disabled?",
    dataIndex: "disabled",
    key: "disabled",
    render: (text) => (
      <Text strong style={{ color: text ? "green" : "red" }}>
        {text ? "Yes" : "No"}
      </Text>
    ),
  },
  {
    title: "Manual Entries Allowed?",
    dataIndex: "manualEntriesAllowed",
    key: "manualEntriesAllowed",
    render: (text) => (
      <Text strong style={{ color: text ? "green" : "red" }}>
        {text ? "Yes" : "No"}
      </Text>
    ),
  },
  {
    title: "Used As",
    dataIndex: "usedAs",
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
const GLA0100Demo = (props) => {
  const navigate = useNavigate();
  const { userData, rightBtn, rightAction } = props;
  const { session } = userData;
  const dispatch = useDispatch();
  const [detailPanelWidth, setDetailPanelWidth] = useState(280);
  const isDragging = React.useRef(false);
  const dragStartX = React.useRef(0);
  const dragStartWidth = React.useRef(0);

  const onDragStart = (e) => {
    isDragging.current = true;
    dragStartX.current = e.clientX;
    dragStartWidth.current = detailPanelWidth;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
  };

  React.useEffect(() => {
    const onMouseMove = (e) => {
      if (!isDragging.current) return;
      const delta = e.clientX - dragStartX.current;
      const next = Math.max(160, Math.min(500, dragStartWidth.current + delta));
      setDetailPanelWidth(next);
    };
    const onMouseUp = () => {
      isDragging.current = false;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };
  }, []);

  // ── List view state (UNCHANGED from original) ──
  const [glList, setGlList] = useState([]);
  const [originalList, setOriginalList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tableCount, setTableCount] = useState("10");
  const [selectedRow, setSelectedRow] = useState(null);

  // ── NEW: raw API objects kept separately for the tree ──
  //   getGlListAction transforms items before storing them in originalList,
  //   so we intercept the raw array with a custom setter wrapper.
  const [rawList, setRawList] = useState([]);

  // ── Tree view state ──
  const [treeData, setTreeData] = useState([]);
  const [selectedNode, setSelectedNode] = useState(null);

  // ── View toggle ──
  const [isTreeView, setIsTreeView] = useState(false);

  // ── Rebuild tree whenever raw data arrives ──
  useEffect(() => {
    if (rawList.length > 0) {
      setTreeData(buildTree(rawList));
    }
  }, [rawList]);

  // ── Expand All ──
  const handleExpandAll = useCallback(() => {
    setTreeData((prev) => {
      const cloned = deepClone(prev);
      flattenTree(cloned).forEach((n) => (n.collapsed = false));
      return cloned;
    });
  }, []);

  // ── Collapse All (keeps only root open) ──
  const handleCollapseAll = useCallback(() => {
    setTreeData((prev) => {
      const cloned = deepClone(prev);
      flattenTree(cloned).forEach((n) => {
        if (!n.isRoot) n.collapsed = true;
      });
      return cloned;
    });
  }, []);

  // ── Toggle single node ──
  const handleToggleNode = useCallback((nodeId) => {
    setTreeData((prev) => toggleById(deepClone(prev), nodeId));
  }, []);

  // ── Select node → detail panel + sidebar buttons ──
  const handleSelectNode = useCallback(
    (node) => {
      setSelectedNode(node);
      setSelectedRow(node); // keep sidebar wired up
      [
        BOM.HasPermission("READ_GLACCOUNT") && cnt.RIGHT_DETAIL_BTN,
        BOM.HasPermission("UPDATE_GLACCOUNT") && cnt.RIGHT_EDIT_BTN,
      ].forEach((type) => type && dispatch({ type }));
    },
    [dispatch],
  );

  // ─────────────────────────────────────────────────────────────────────────
  // List-view helpers — UNCHANGED from original
  // ─────────────────────────────────────────────────────────────────────────
  function handleStatusChange(value) {
    let tempList = originalList;
    switch (value.toLowerCase()) {
      case "active":
        tempList = originalList.filter((i) => i.disabled === false);
        break;
      case "disabled":
        tempList = originalList.filter((i) => i.disabled === true);
        break;
      default:
        return;
    }
    setGlList(tempList);
  }

  function handleSearchInTable(e) {
    const searchKey = e.target.value;
    if (!searchKey) {
      setGlList(originalList);
      return;
    }
    setGlList(
      originalList.filter(
        (item) =>
          item?.key === parseInt(searchKey) ||
          item?.account?.toLowerCase()?.includes(searchKey.toLowerCase()) ||
          item?.glCode?.toLowerCase()?.includes(searchKey.toLowerCase()) ||
          item?.accountType?.toLowerCase()?.includes(searchKey.toLowerCase()),
      ),
    );
  }

  function selectTableCount(value) {
    setTableCount(value);
  }

  const onSelectItemAction = (selectedRowKeys, objParam) => {
    setSelectedRow(objParam[0]);
    [
      BOM.HasPermission("READ_GLACCOUNT") && cnt.RIGHT_DETAIL_BTN,
      BOM.HasPermission("UPDATE_GLACCOUNT") && cnt.RIGHT_EDIT_BTN,
    ].forEach((type) => type && dispatch({ type }));
  };

  function handleOnView() {
    if (rightBtn.detailBtn?.toLowerCase() === "primary")
      navigate("/view-gla0100", { state: selectedRow });
    else console.log("Please choose a record from the table to open");
  }
  function handleOnEdit() {
    if (rightBtn.editBtn?.toLowerCase() === "primary")
      navigate("/edit-gla0100", { state: selectedRow });
    else console.log("Please choose a record from the table to open");
  }
  function handleOnCreate() {
    if (rightBtn.addBtn?.toLowerCase() === "primary")
      navigate("/add-gla0100", { ofObj: selectedRow });
    else console.log("Could not open the link");
  }

  useEffect(() => {
    dispatch({ type: cnt.CLEAR_RIGHT_BTN });
    BOM.HasPermission("CREATE_GLACCOUNT") && rightAction(cnt.RIGHT_ADD_BTN);

    const params = { limit: 1000, offset: 0, orderBy: "id", sortOrder: "DESC" };

    BOM.FetchReqAction(
      { session, params },
      definedApi.GlAccount,
      (err, res) => {
        if (err) {
          BOM.AlertMsg(cnt.DANGER, err?.errors?.[0]?.defaultUserMessage);
          setLoading(false);
          return;
        }
        const pageItems = res?.pageItems ?? [];

        setRawList(pageItems);

        const tableItems = pageItems.map((element) => ({
          key: element?.id,
          account: element?.name ?? "No description",
          glCode: element?.glCode ?? "No Code",
          accountType: element?.type?.value,
          disabled: element?.disabled,
          manualEntriesAllowed: element?.manualEntriesAllowed,
          usedAs: element?.usage?.value,
        }));
        setGlList(tableItems.filter((item) => item.disabled === false));
        setOriginalList(tableItems);
        setLoading(false);
      },
    );
  }, []);

  return (
    <MainLayout
      isEnableRightSidebar={!isTreeView}
      onView={!isTreeView ? handleOnView : undefined}
      onAdd={handleOnCreate}
      onEdit={!isTreeView ? handleOnEdit : undefined}
    >
      <TopContent
        pageTitle={appLabel.generalLedger}
        pageItems={manageGeneralLedgerItems}
      />

      {/* ── Card — header unchanged, extra slot gets the toggle button ── */}
      <Card
        title={"General Ledger List"}
        extra={
          <Space>
            <Button
              type="primary"
              className="of-primaryBtnColor"
              icon={
                isTreeView ? <UnorderedListOutlined /> : <ApartmentOutlined />
              }
              onClick={() => {
                setIsTreeView((v) => !v);
                setSelectedNode(null);
              }}
            >
              {isTreeView ? "List View" : "Tree View"}
            </Button>
            {!isTreeView && (
              <Col>
                <StatusSelect handleStatusChange={handleStatusChange} />
              </Col>
            )}
          </Space>
        }
        style={{
          boxShadow: "0px 0px 30px 3px rgba(0,0,0,0.1)",
          borderRadius: 20,
        }}
      >
        <Spin spinning={loading} size="large">
          {/* ════════════ LIST VIEW — 100% original code ════════════ */}
          {!isTreeView && (
            <>
              <Row style={{ marginBottom: 20 }}>
                <div style={{ marginBottom: "auto" }}>
                  <Text>Show</Text>
                  <Select
                    defaultValue={tableCount}
                    options={[
                      { value: "5", label: "5" },
                      { value: "10", label: "10" },
                      { value: "25", label: "25" },
                      { value: "50", label: "50" },
                    ]}
                    onSelect={selectTableCount}
                  />
                  <Text>Entries</Text>
                </div>
                <div style={{ margin: "0px 50px" }}>
                  <Input
                    onChange={handleSearchInTable}
                    placeholder="Search by GL Code or Account Name or Account Type..."
                    style={{ width: 400 }}
                  />
                </div>
              </Row>
              <Table
                rowSelection={{ type: "radio", onChange: onSelectItemAction }}
                columns={columns}
                dataSource={glList}
                bordered
                pagination={{
                  position: "bottomCentre",
                  pageSize: tableCount,
                }}
              />
            </>
          )}

          {/* ════════════ TREE VIEW ════════════ */}
          {isTreeView && (
            <div style={{ display: "flex", gap: 0, minHeight: 500 }}>
              {/* LEFT: detail panel */}
              <div
                style={{
                  width: detailPanelWidth,
                  flexShrink: 0,
                  background: "#fafafa",
                  borderRadius: "0 0 0 16px",
                  display: "flex",
                  flexDirection: "row",
                }}
              >
                <div style={{ flex: 1, overflow: "hidden" }}>
                  <GLDetailPanel node={selectedNode} />
                </div>
                {/* Drag handle */}
                <div
                  onMouseDown={onDragStart}
                  style={{
                    width: 5,
                    cursor: "col-resize",
                    background: "transparent",
                    borderRight: "2px solid #f0f0f0",
                    flexShrink: 0,
                    transition: "border-color 0.15s",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.borderColor = "#15CCAE")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.borderColor = "#f0f0f0")
                  }
                />
              </div>

              {/* RIGHT: tree */}
              <div
                style={{
                  flex: 1,
                  overflow: "hidden",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                {/* Expand / Collapse All toolbar */}
                <div
                  style={{
                    padding: "8px 16px",
                    borderBottom: "1px solid #f0f0f0",
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    background: "#fafafa",
                  }}
                >
                  <Link
                    onClick={handleExpandAll}
                    style={{ fontSize: 12, color: "#15CCAE" }}
                  >
                    Expand All
                  </Link>
                  <Text style={{ color: "#d9d9d9" }}>/</Text>
                  <Link
                    onClick={handleCollapseAll}
                    style={{ fontSize: 12, color: "#15CCAE" }}
                  >
                    Collapse All
                  </Link>
                </div>

                {/* Scrollable tree */}
                <div
                  style={{
                    flex: 1,
                    overflowY: "auto",
                    padding: "8px 8px 16px 8px",
                    maxHeight: "62vh",
                  }}
                >
                  {treeData.map((root) => (
                    <TreeNode
                      key={root.id}
                      node={root}
                      depth={0}
                      onToggle={handleToggleNode}
                      onSelectNode={handleSelectNode}
                      selectedId={selectedNode?.id ?? null}
                    />
                  ))}
                </div>
              </div>
            </div>
          )}
        </Spin>
      </Card>
    </MainLayout>
  );
};

const mapStateToProps = (state) => ({
  userData: state.AuthenticationData.userData,
  rightBtn: state.RightSideBtn,
});
const mapDispatchToProps = { rightAction };
export default connect(mapStateToProps, mapDispatchToProps)(GLA0100Demo);
